import { Badge, Box, Group, Text, Tooltip } from "@mantine/core";
import {
  IconUser, IconBriefcase, IconBuilding, IconBulb, IconTag, IconLanguage,
  IconMapPin, IconFileText, IconSparkles, IconUsers,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { languageLabel } from "../constants/languages";

// Maps each backend MatchField (camelCase) to its chip icon. Concrete field hits get a
// representative icon; the holistic fallbacks (profile/document/semantic/similar) get a
// document or sparkle so they read as "AI / fuzzy" rather than a precise field hit.
const FIELD_ICON = {
  name: IconUser,
  title: IconBriefcase,
  company: IconBuilding,
  skill: IconBulb,
  tag: IconTag,
  language: IconLanguage,
  location: IconMapPin,
  summary: IconFileText,
  profile: IconFileText,
  document: IconFileText,
  fuzzy: IconSparkles,
  semantic: IconSparkles,
  similar: IconUsers,
};

// The holistic signals carry no concrete value — their chip text is the field label itself.
const HOLISTIC = new Set(["profile", "document", "fuzzy", "semantic", "similar"]);

// name/title matches are already conveyed by the in-text query <Highlight> on the row, so showing
// a chip for them too is redundant noise — drop them and let the chips carry only the non-obvious
// reasons (company, skill, semantic, …).
const REDUNDANT_WITH_HIGHLIGHT = new Set(["name", "title"]);

// Renders the "why was this contact found" signals returned by the search backend as compact,
// dimmed chips (e.g. 🏢 Russell Reynolds Associates · 💡 Executive Search). Concrete hits show
// the matched value; fuzzy hits show a labelled reason ("Semantic match", "In document").
// Hovering a chip names the field it matched in. Caps to `max` with a "+N" overflow.
export default function MatchReasons({ matches, max = 3, ...groupProps }) {
  const { t } = useTranslation("Veft");
  const visible = (matches ?? []).filter((m) => !REDUNDANT_WITH_HIGHLIGHT.has(m.field));
  if (visible.length === 0) return null;

  const shown = visible.slice(0, max);
  const overflow = visible.length - shown.length;

  const chipText = (m) => {
    if (HOLISTIC.has(m.field) || !m.value) return t(`match_field_${m.field}`);
    if (m.field === "language") return languageLabel(m.value);
    return m.value;
  };

  return (
    <Group gap={6} wrap="wrap" align="center" {...groupProps}>
      <Text size="xs" c="dimmed" fw={500} style={{ flexShrink: 0 }}>
        {t("search_match_prefix")}
      </Text>
      {shown.map((m, i) => {
        const Icon = FIELD_ICON[m.field] ?? IconSparkles;
        const key = `${m.field}-${m.value ?? ""}-${i}`;
        const badge = (
          <Badge
            size="sm"
            variant="light"
            color="gray"
            tt="none"
            fw={400}
            leftSection={<Icon size={11} />}
            style={{ cursor: "default", maxWidth: 240 }}
          >
            {chipText(m)}
          </Badge>
        );
        // Holistic chips already read as a full reason ("Semantic match") — a "Matched in …"
        // tooltip on top would double-prefix into nonsense, so they get no tooltip. Concrete
        // chips show the matched value and the tooltip names the field it matched in.
        return HOLISTIC.has(m.field) ? (
          <Box key={key} component="span" style={{ display: "inline-flex" }}>{badge}</Box>
        ) : (
          <Tooltip key={key} label={t("search_matched_in", { field: t(`match_field_${m.field}`) })} withArrow openDelay={200}>
            {badge}
          </Tooltip>
        );
      })}
      {overflow > 0 && (
        <Badge size="sm" variant="light" color="gray" tt="none" fw={400}>
          +{overflow}
        </Badge>
      )}
    </Group>
  );
}
